import React, { useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography
} from '@material-ui/core';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import PeopleIcon from '@material-ui/icons/People';
import PaletteIcon from '@material-ui/icons/Palette';
import PublishIcon from '@material-ui/icons/Publish';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import WhatshotIcon from '@material-ui/icons/Whatshot';
import PublishJobConfirmationDialog from '../../../../components/PublishJobConfirmationDialog/PublishJobConfirmationDialog';
import NoCreditsBuyBundleDialog from '../../../../components/NoCreditsBuyBundleDialog/NoCreditsBuyBundleDialog';
import {
  APP_PATH_EDITOR_JOB,
  APP_PATH_JOB_ADDONS,
  APP_PATH_PACKAGE_PLAN,
  APP_PATH_PLAN_CONFIGURE,
  CATEGORIES_JOB,
  PLAN_TYPE,
  TYPES_JOB
} from '../../../../config';
import { STATUS_ACTIVE } from '../../../../common/adapters';
import { getTimeFromXDisplay } from '../../../../helpers';
import RichEditorReadOnly from '../../../../components/RichEditorReadOnly/RichEditorReadOnly';
import { applicationsSelector } from '../../../../redux/features/applications/applicationsSlice';
import { buildJobUrl } from '../../../../helpers/urlHelpers';
import {
  bundleHistorySelector,
  bundleStatusSelector,
  hasCreditsSelector,
  remainingCreditsSelector
} from '../../../../redux/features/bundle/bundleSlice';

const useStyles = makeStyles((theme) => ({
  root: {},
  content: {
    padding: theme.spacing(2)
  },
  jobCard: {
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1.5),
    overflow: 'hidden'
  },
  jobHeader: {
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.background.section
  },
  jobTitle: {
    fontWeight: 700,
    fontSize: '1.8rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    fontFamily: '"Neufile Grotesk Semi Bold", sans-serif'
  },
  jobMetadata: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  chipTag: {
    textTransform: 'uppercase',
    fontWeight: 600,
    fontSize: '0.75rem'
  },
  timePosted: {
    textTransform: 'uppercase',
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
  },
  jobContent: {
    padding: theme.spacing(3),
    maxHeight: '200px',
    overflow: 'hidden',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '50px',
      background: `linear-gradient(transparent, ${theme.palette.background.paper})`
    }
  },
  actionsBar: {
    padding: theme.spacing(2.5),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  addOnsCta: {
    marginRight: theme.spacing(2),
    borderRadius: 999,
    padding: theme.spacing(0.75, 2),
    textTransform: 'none',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #fff7e6 0%, #ffffff 60%)',
    border: '1px solid rgba(255, 191, 0, 0.45)',
    color: theme.palette.text.primary,
    boxShadow: '0 10px 20px -16px rgba(255, 179, 0, 0.7)',
    '&:hover': {
      background: 'linear-gradient(135deg, #ffe7b3 0%, #ffffff 70%)',
      borderColor: 'rgba(255, 179, 0, 0.7)'
    }
  },
  addOnsCtaIcon: {
    marginRight: theme.spacing(1),
    color: '#f1a000'
  },
  brandColorCta: {
    marginLeft: theme.spacing(1.5),
    borderRadius: 999,
    padding: theme.spacing(0.75, 2),
    textTransform: 'none',
    fontWeight: 700,
    border: `1px solid ${theme.palette.primary.main}30`,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.background.paper}70 100%)`,
    color: theme.palette.text.primary,
    boxShadow: '0 10px 20px -16px rgba(63, 81, 181, 0.5)',
    '&:hover': {
      borderColor: `${theme.palette.primary.main}60`,
      boxShadow: '0 12px 22px -16px rgba(63, 81, 181, 0.7)'
    }
  },
  brandColorCtaIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main
  },
  actionButton: {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main
    }
  },
  actionMenuPaper: {
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[4],
    minWidth: 190
  },
  actionMenuItem: {
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1),
    margin: theme.spacing(0.5, 1),
    '&:hover': {
      backgroundColor: theme.palette.background.default
    }
  },
  actionMenuIcon: {
    minWidth: 36,
    color: theme.palette.text.secondary
  },
  publishCta: {
    borderRadius: 999,
    padding: theme.spacing(0.75, 2),
    textTransform: 'none',
    fontWeight: 700,
    boxShadow: '0 12px 24px -18px rgba(63, 81, 181, 0.6)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 16px 28px -18px rgba(63, 81, 181, 0.7)'
    }
  },
  unpublishCta: {
    borderRadius: 999,
    padding: theme.spacing(0.75, 2),
    textTransform: 'none',
    fontWeight: 700
  },
  toggleSwitch: {
    marginRight: theme.spacing(2)
  },
  geoLockToggle: {
    marginLeft: theme.spacing(2),
    padding: theme.spacing(0.5, 1.2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    backgroundColor: theme.palette.background.default
  },
  geoLockIcon: {
    fontSize: '1.2rem',
    marginRight: theme.spacing(0.5),
    verticalAlign: 'middle',
    color: theme.palette.warning.main
  },
  geoLockLabel: {
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  emptyState: {
    padding: theme.spacing(8),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  candidatesButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.6, 1.1),
    borderRadius: 999,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default
  },
  candidatesCount: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    minWidth: '18px',
    textAlign: 'center'
  },
  candidatesIcon: {
    fontSize: '1.1rem',
    color: theme.palette.text.secondary
  }
}));

const JobListByUser = (props) => {
  const classes = useStyles();
  const {
    className,
    jobs,
    enrichedJobs,
    editJob,
    toggleJobStatus,
    toggleGeoLock,
    deleteJob,
    updatingJobId,
    history
  } = props;

  // Get applications from Redux
  const applications = useSelector(applicationsSelector);

  // Bundle status selectors
  const bundleStatus = useSelector(bundleStatusSelector);
  const hasCredits = useSelector(hasCreditsSelector);
  const remainingCredits = useSelector(remainingCreditsSelector);
  const bundleHistory = useSelector(bundleHistorySelector);

  const totalRemainingCredits = React.useMemo(() => {
    if (!bundleHistory || bundleHistory.length === 0) {
      return remainingCredits;
    }
    return bundleHistory.reduce(
      (sum, entry) => sum + (entry.remainingCredits || 0),
      0
    );
  }, [bundleHistory, remainingCredits]);

  const bundlePlanLabel = React.useMemo(() => {
    if (!bundleHistory || bundleHistory.length === 0) {
      return bundleStatus.planName || 'Unknown';
    }
    if (bundleHistory.length === 1) {
      return bundleHistory[0]?.planName || bundleStatus.planName || 'Unknown';
    }
    return 'Multiple bundles';
  }, [bundleHistory, bundleStatus.planName]);

  // Count applications per job
  const getApplicationCount = (jobId) => {
    return applications.filter((app) => app.jobId === jobId).length;
  };

  // Debug logging
  console.log('🔍 [JobListByUser] Rendered with:');
  console.log('🔍 [JobListByUser] jobs:', jobs);
  console.log('🔍 [JobListByUser] enrichedJobs:', enrichedJobs);
  console.log(
    '🔍 [JobListByUser] PLAN_TYPE.CREATE_QR_CODE:',
    PLAN_TYPE.CREATE_QR_CODE
  );
  console.log('🔍 [JobListByUser] applications:', applications);
  console.log('💳 [JobListByUser] bundleStatus:', bundleStatus);
  console.log('💳 [JobListByUser] hasCredits:', hasCredits);

  // Track which job's geo-lock is being toggled
  const [togglingGeoLockJobId, setTogglingGeoLockJobId] = useState(null);

  // Publish dialog state
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [noCreditsDialogOpen, setNoCreditsDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuJob, setActionMenuJob] = useState(null);

  const handleEdit = (job) => {
    editJob(job);
    history.push(APP_PATH_EDITOR_JOB);
  };

  // Publish/Unpublish handlers
  const handlePublishClick = (job) => {
    const isCurrentlyActive = job.status === STATUS_ACTIVE;

    if (isCurrentlyActive) {
      // Unpublish - no credit check needed
      console.log('📤 [JobListByUser] Unpublishing job:', job.title);
      toggleJobStatus(job);
    } else {
      // Publish - check credits
      console.log('💳 [JobListByUser] Publishing job, checking credits...');
      console.log('💳 [JobListByUser] hasCredits:', hasCredits);
      console.log('💳 [JobListByUser] remainingCredits:', remainingCredits);

      if (hasCredits) {
        console.log(
          '✅ [JobListByUser] Credits available, showing confirmation'
        );
        setSelectedJob(job);
        setPublishDialogOpen(true);
      } else {
        console.log('❌ [JobListByUser] No credits, redirect to buy pack');
        history.push(APP_PATH_PACKAGE_PLAN);
      }
    }
  };

  const handleConfirmPublish = () => {
    if (selectedJob) {
      console.log(
        '✅ [JobListByUser] Confirmed publish for:',
        selectedJob.title
      );
      toggleJobStatus(selectedJob);
      setPublishDialogOpen(false);
      setSelectedJob(null);
    }
  };

  const handleClosePublishDialog = () => {
    console.log('❌ [JobListByUser] Cancelled publish dialog');
    setPublishDialogOpen(false);
    setSelectedJob(null);
  };

  const handleCloseNoCreditsDialog = () => {
    console.log('❌ [JobListByUser] Closed no credits dialog');
    setNoCreditsDialogOpen(false);
  };

  const handleOpenActionMenu = (event, job) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuJob(job);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuJob(null);
  };

  const isActiveJob = (job) => {
    return job.status === STATUS_ACTIVE;
  };

  const getTypeJob = (job) => {
    let type = '';
    if (job && job.type) {
      const typeFound = TYPES_JOB.find((t) => t.value === job.type);
      if (typeFound) {
        type = typeFound.label;
      }
    }
    return type;
  };

  const getCategory = (job) => {
    let category = '';
    if (job && job.category) {
      const categoryFound = CATEGORIES_JOB.find(
        (c) => c.value === job.category
      );
      if (categoryFound) {
        category = categoryFound.label;
      }
    }
    return category;
  };

  // Check if job has geo-lock plan purchased
  const hasGeoLockPlan = (jobId) => {
    const enrichedJob = enrichedJobs?.find((ej) => ej.job?.id === jobId);

    console.log(`🔍 [hasGeoLockPlan] Checking job ${jobId}`);

    if (!enrichedJob) {
      console.log(`🔍 [hasGeoLockPlan] No enrichedJob found for job ${jobId}`);
      return false;
    }

    // Check if job has GEO_LOCK benefit (from hiring profile or standalone purchase)
    const hasGeoLockBenefit =
      enrichedJob.job?.activeBenefits?.GEO_LOCK === true;
    console.log(
      `🔍 [hasGeoLockPlan] Result for job ${jobId}:`,
      hasGeoLockBenefit
    );
    return hasGeoLockBenefit;
  };

  // Check if geo-lock is currently enabled (user's preference)
  const isGeoLockEnabled = (jobId) => {
    const enrichedJob = enrichedJobs?.find((ej) => ej.job?.id === jobId);
    console.log(`🔍 [isGeoLockEnabled] Checking job ${jobId}`);

    if (!enrichedJob || !enrichedJob.plan) {
      console.log(
        `❌ [isGeoLockEnabled] No enrichedJob/plan found for ${jobId}`
      );
      return false;
    }

    // Check plan.metadata.geoLockEnabled (user's toggle preference)
    const isEnabled = enrichedJob.plan?.metadata?.geoLockEnabled === true;
    console.log(
      `🔍 [isGeoLockEnabled] plan.metadata.geoLockEnabled: ${isEnabled}`
    );
    return isEnabled;
  };

  // Get the plan for a job
  const getJobPlan = (jobId) => {
    const enrichedJob = enrichedJobs?.find((ej) => ej.job?.id === jobId);
    return enrichedJob?.plan;
  };

  const hasBrandColorBenefit = (jobId) => {
    const enrichedJob = enrichedJobs?.find((ej) => ej.job?.id === jobId);
    return enrichedJob?.job?.activeBenefits?.HIGHLIGHT_BRAND === true;
  };

  // Handle geo-lock toggle
  const handleToggleGeoLock = async (job) => {
    const plan = getJobPlan(job.id);
    if (!plan) {
      console.warn('No plan found for job:', job.id);
      return;
    }

    setTogglingGeoLockJobId(job.id);
    try {
      await toggleGeoLock(job, plan);
    } finally {
      setTogglingGeoLockJobId(null);
    }
  };

  return (
    <Card className={clsx(classes.root, className)}>
      <CardHeader
        title="Jobs"
        subheader={`${jobs.length} job${jobs.length !== 1 ? 's' : ''} posted`}
      />
      <Divider />
      <CardContent className={classes.content}>
        <Grid container spacing={3}>
          {jobs.map((job) => {
            const isActive = isActiveJob(job);
            const typeJob = getTypeJob(job);
            const category = getCategory(job);
            const jobCreatedTime = job.createdAt || new Date();
            const creationDate = `Posted ${getTimeFromXDisplay(
              jobCreatedTime
            )}`;
            const title = job.title || '';
            const details = job.detail || null;
            const isUpdating = updatingJobId === job.id;
            const hasGeoLock = hasGeoLockPlan(job.id);
            const geoLockEnabled = isGeoLockEnabled(job.id);
            const isTogglingGeoLock = togglingGeoLockJobId === job.id;
            const applicationCount = getApplicationCount(job.id);
            return (
              <Grid item xs={12} key={job.id}>
                <Card className={classes.jobCard} elevation={2}>
                  {/* Job Header */}
                  <div className={classes.jobHeader}>
                    <Typography className={classes.timePosted} variant="body2">
                      {creationDate}
                    </Typography>
                    <Typography className={classes.jobTitle} variant="h2">
                      {title}
                    </Typography>

                    {/* Metadata Chips */}
                    <div className={classes.jobMetadata}>
                      {typeJob && (
                        <Chip
                          className={classes.chipTag}
                          label={typeJob}
                          size="small"
                          color="primary"
                        />
                      )}
                      {category && (
                        <Chip
                          className={classes.chipTag}
                          label={category}
                          size="small"
                          color="secondary"
                        />
                      )}
                    </div>
                  </div>

                  {/* Job Content Preview */}
                  {details && (
                    <div className={classes.jobContent}>
                      <RichEditorReadOnly content={details} />
                    </div>
                  )}

                  {/* Actions Bar */}
                  <div className={classes.actionsBar}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {/* Publish/Unpublish Button */}
                      <Tooltip
                        title={
                          isActive
                            ? 'Unpublishing does not use credits.'
                            : job.hasPostJobPlanPaid
                            ? 'No credits needed to publish — a credit was already used for this job.'
                            : 'Publishing will use 1 credit. After that, you can publish or unpublish this job without additional credits.'
                        }
                        arrow>
                        <span>
                          <Button
                            className={
                              isActive
                                ? classes.unpublishCta
                                : classes.publishCta
                            }
                            variant={isActive ? 'outlined' : 'contained'}
                            color="primary"
                            size="small"
                            startIcon={
                              isActive ? <VisibilityOffIcon /> : <PublishIcon />
                            }
                            onClick={() => handlePublishClick(job)}
                            disabled={isUpdating}
                            style={{ marginRight: 16, minWidth: 120 }}>
                            {isUpdating
                              ? 'Updating...'
                              : isActive
                              ? 'Unpublish'
                              : 'Publish'}
                          </Button>
                        </span>
                      </Tooltip>

                      <Button
                        className={classes.addOnsCta}
                        size="small"
                        onClick={() => {
                          history.push(
                            `${APP_PATH_JOB_ADDONS}?jobId=${job.id}`
                          );
                        }}>
                        <WhatshotIcon className={classes.addOnsCtaIcon} />
                        Boost with add-ons
                      </Button>

                      {hasBrandColorBenefit(job.id) && (
                        <Button
                          className={classes.brandColorCta}
                          size="small"
                          onClick={() => {
                            history.push(
                              APP_PATH_PLAN_CONFIGURE.replace(':jobId', job.id)
                            );
                          }}>
                          <PaletteIcon className={classes.brandColorCtaIcon} />
                          Edit brand color
                        </Button>
                      )}

                      {/* Geo-lock Toggle (only show if plan is purchased) */}
                      {hasGeoLock && (
                        <Tooltip
                          title={`Geo-lock restricts this job to: ${
                            job.regionalRestrictions || 'specified regions'
                          }`}
                          arrow>
                          <FormControlLabel
                            className={classes.geoLockToggle}
                            control={
                              <Switch
                                checked={geoLockEnabled}
                                onChange={() => handleToggleGeoLock(job)}
                                color="secondary"
                                name="geoLock"
                                disabled={isTogglingGeoLock || isUpdating}
                                size="small"
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                className={classes.geoLockLabel}>
                                <LocationOnIcon
                                  className={classes.geoLockIcon}
                                />
                                {isTogglingGeoLock
                                  ? 'Updating...'
                                  : geoLockEnabled
                                  ? 'Geo-lock ON'
                                  : 'Geo-lock OFF'}
                              </Typography>
                            }
                          />
                        </Tooltip>
                      )}
                    </div>

                    <div className={classes.actionButtons}>
                      <Tooltip
                        title={`View ${applicationCount} candidate${
                          applicationCount !== 1 ? 's' : ''
                        }`}>
                        <IconButton
                          className={classes.actionButton}
                          aria-label="candidates"
                          size="small"
                          onClick={() => {
                            history.push(`/applications/job/${job.id}`);
                          }}>
                          <div className={classes.candidatesButton}>
                            <PeopleIcon className={classes.candidatesIcon} />
                            <Typography className={classes.candidatesCount}>
                              {applicationCount}
                            </Typography>
                          </div>
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        className={classes.actionButton}
                        aria-label="more actions"
                        size="small"
                        onClick={(event) => handleOpenActionMenu(event, job)}>
                        <MoreVertIcon />
                      </IconButton>
                    </div>
                  </div>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        classes={{ paper: classes.actionMenuPaper }}
        keepMounted>
        <MenuItem
          className={classes.actionMenuItem}
          onClick={() => {
            const job = actionMenuJob;
            handleCloseActionMenu();
            if (job) {
              const companyName = enrichedJobs?.find(
                (jobResult) => jobResult.job?.id === job.id
              )?.company?.name;
              const jobUrl = buildJobUrl(job.id, job.title, companyName);
              window.open(jobUrl, '_blank');
            }
          }}>
          <ListItemIcon className={classes.actionMenuIcon}>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View job" />
        </MenuItem>
        <MenuItem
          className={classes.actionMenuItem}
          onClick={() => {
            const job = actionMenuJob;
            handleCloseActionMenu();
            if (job) {
              handleEdit(job);
            }
          }}>
          <ListItemIcon className={classes.actionMenuIcon}>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit job" />
        </MenuItem>
        <MenuItem
          className={classes.actionMenuItem}
          onClick={() => {
            const job = actionMenuJob;
            handleCloseActionMenu();
            if (job) {
              deleteJob(job);
            }
          }}
          disabled={updatingJobId === actionMenuJob?.id}>
          <ListItemIcon className={classes.actionMenuIcon}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete job" />
        </MenuItem>
      </Menu>

      {/* Publish Confirmation Dialog */}
      <PublishJobConfirmationDialog
        open={publishDialogOpen}
        onClose={handleClosePublishDialog}
        onConfirm={handleConfirmPublish}
        jobTitle={selectedJob?.title || ''}
        planName={bundlePlanLabel}
        remainingCredits={totalRemainingCredits}
        isPublishing={updatingJobId === selectedJob?.id}
        hasPostJobPlanPaid={selectedJob?.hasPostJobPlanPaid}
      />

      {/* No Credits Dialog */}
      <NoCreditsBuyBundleDialog
        open={noCreditsDialogOpen}
        onClose={handleCloseNoCreditsDialog}
      />
    </Card>
  );
};

JobListByUser.defaultProps = {
  jobs: []
};

JobListByUser.propTypes = {
  className: PropTypes.string,
  jobs: PropTypes.array,
  enrichedJobs: PropTypes.array,
  editJob: PropTypes.func.isRequired,
  toggleJobStatus: PropTypes.func.isRequired,
  toggleGeoLock: PropTypes.func.isRequired,
  deleteJob: PropTypes.func.isRequired,
  updatingJobId: PropTypes.string,
  history: PropTypes.object.isRequired
};

export default withRouter(JobListByUser);
